import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Moon, Sun } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import Button from './ui/Button';
import Input from './ui/Input';
import { trendingService } from '../services/trendingService';

const Navbar = ({ sidebarOpen, setSidebarOpen }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchSuggestions, setSearchSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const navigate = useNavigate();
  const searchRef = React.useRef(null);

  // Handle click outside to close suggestions
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch search suggestions as user types
  React.useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.trim().length > 1) {
        setIsSearching(true);
        try {
          const results = await trendingService.searchTrends(searchQuery);
          setSearchSuggestions(results.slice(0, 5)); // Show top 5 suggestions
          setShowSuggestions(true);
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearch = (query) => {
    if (query.trim()) {
      navigate(`/trends?search=${encodeURIComponent(query.trim())}`);
      setShowSuggestions(false);
      setSearchQuery('');
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.topic);
    handleSearch(suggestion.topic);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-dark-card border-b border-reddit-200 dark:border-dark-border">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Logo and Menu */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-reddit-100 dark:hover:bg-dark-border transition-colors"
            >
              {sidebarOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
            
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-reddit-orange rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">T</span>
              </div>
              <span className="text-xl font-bold gradient-text hidden sm:block">
                Trendy
              </span>
            </Link>
          </div>

          {/* Center - Search Bar */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8">
            <div className="relative w-full" ref={searchRef}>
              <Input
                type="text"
                placeholder="Search trending topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch(searchQuery);
                  }
                }}
                onFocus={() => searchQuery.length > 1 && setShowSuggestions(true)}
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-reddit-500" />
              
              {/* Search Suggestions Dropdown */}
              {showSuggestions && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-dark-card border border-reddit-200 dark:border-dark-border rounded-lg shadow-reddit-hover max-h-96 overflow-y-auto z-50">
                  {isSearching ? (
                    <div className="p-4 text-center text-reddit-600 dark:text-dark-textMuted">
                      <div className="animate-pulse">Searching...</div>
                    </div>
                  ) : searchSuggestions.length > 0 ? (
                    <div className="py-2">
                      <div className="px-4 py-2 text-xs font-semibold text-reddit-600 dark:text-dark-textMuted uppercase">
                        Suggestions
                      </div>
                      {searchSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="w-full px-4 py-3 text-left hover:bg-reddit-50 dark:hover:bg-dark-border transition-colors flex items-center justify-between"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-sm">{suggestion.topic}</div>
                            <div className="text-xs text-reddit-600 dark:text-dark-textMuted mt-1">
                              {suggestion.mentions.toLocaleString()} mentions • {suggestion.category}
                            </div>
                          </div>
                          <Search className="w-4 h-4 text-reddit-400" />
                        </button>
                      ))}
                    </div>
                  ) : searchQuery.length > 1 ? (
                    <div className="p-4 text-center text-reddit-600 dark:text-dark-textMuted text-sm">
                      No suggestions found for "{searchQuery}"
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-reddit-100 dark:hover:bg-dark-border transition-colors"
              aria-label="Toggle theme"
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
            
            <Link to="/trends">
              <Button variant="primary" size="sm" className="hidden sm:flex">
                Get Started
              </Button>
            </Link>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="md:hidden pb-4">
          <div className="relative" ref={searchRef}>
            <Input
              type="text"
              placeholder="Search trends..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(searchQuery);
                }
              }}
              onFocus={() => searchQuery.length > 1 && setShowSuggestions(true)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-reddit-500" />
            
            {/* Mobile Search Suggestions */}
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-dark-card border border-reddit-200 dark:border-dark-border rounded-lg shadow-reddit-hover max-h-96 overflow-y-auto z-50">
                {isSearching ? (
                  <div className="p-4 text-center text-reddit-600 dark:text-dark-textMuted">
                    <div className="animate-pulse">Searching...</div>
                  </div>
                ) : searchSuggestions.length > 0 ? (
                  <div className="py-2">
                    <div className="px-4 py-2 text-xs font-semibold text-reddit-600 dark:text-dark-textMuted uppercase">
                      Suggestions
                    </div>
                    {searchSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.id}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-reddit-50 dark:hover:bg-dark-border transition-colors"
                      >
                        <div className="font-medium text-sm">{suggestion.topic}</div>
                        <div className="text-xs text-reddit-600 dark:text-dark-textMuted mt-1">
                          {suggestion.mentions.toLocaleString()} mentions • {suggestion.category}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : searchQuery.length > 1 ? (
                  <div className="p-4 text-center text-reddit-600 dark:text-dark-textMuted text-sm">
                    No suggestions found
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;